import { object, bool } from "prop-types";
import { ResponsiveImage } from "@quintype/components";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "@quintype/components";

import ScrollSnap from "../../../molecules/ScrollSnap";
import TriangleArrow from "../../../atoms/icons/triangle-arrow";

import "./slideshow-element.m.css";
import { pushEventToDataLayer } from "../../../helper/datalayer-utils";

const Slideshow = ({ element, auto = false }) => {
  const [arrowTop, setArrowTop] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const items = element?.["story-elements"] || [];
  const slideCount = items.length;

  const handlePageView = useCallback(() => {
    if (window?.dataLayer) pushEventToDataLayer("slideShow");
  }, []);

  const calculateArrowTop = useCallback(() => {
    const ImageHeight = document.querySelector("#slideshow-image-wrapper")?.clientHeight || 0;
    const arrowHeight = window.innerWidth < 768 ? 25 : 45;
    setArrowTop(`${ImageHeight / 2 - arrowHeight}px`);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      calculateArrowTop();
      window.addEventListener("resize", calculateArrowTop);
      return () => window.removeEventListener("resize", calculateArrowTop);
    }
  }, [calculateArrowTop]);

  // autoplay helpers
  const startAuto = useCallback(() => {
    if (!auto || slideCount <= 1) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % slideCount;
        handlePageView();
        return next;
      });
    }, 5000);
  }, [auto, slideCount, handlePageView]);

  useEffect(() => {
    if (auto && slideCount > 1) startAuto();
    return () => clearInterval(intervalRef.current);
  }, [auto, slideCount, startAuto]);

  // when parent-controlled index changes (from interval or user swipe), restart autoplay
  const handleIndexChange = (index) => {
    setCurrentIndex(index);
    if (auto) {
      // restart timer so user interaction resets the 5s timer
      clearInterval(intervalRef.current);
      startAuto();
    }
  };

  const children = items.map((item, index) => (
    <div key={index}>
      <div id="slideshow-image-wrapper">
        <figure className="blank-story-image" styleName="qt-image-16x9">
          {item?.hyperlink ? (
            <Link href={item?.hyperlink}>
              <ResponsiveImage
                slug={decodeURIComponent(item["image-s3-key"])}
                metadata={item["image-metadata"]}
                aspectRatio={[16, 9]}
                widths={[125, 240, 320]}
                imgParams={{ auto: ["format", "compress"], fit: "max" }}
                alt={item["image-attribution"] || item.title || ""}
              />
            </Link>
          ) : (
            <ResponsiveImage
              slug={decodeURIComponent(item["image-s3-key"])}
              metadata={item["image-metadata"]}
              aspectRatio={[16, 9]}
              widths={[125, 240, 320]}
              imgParams={{ auto: ["format", "compress"], fit: "max" }}
              alt={item["image-attribution"] || item.title || ""}
            />
          )}
        </figure>
      </div>
      {item.title && (
        <div styleName="scroll-caption-wrapper">
          <figcaption styleName="caption" dangerouslySetInnerHTML={{ __html: item.title }} />
          {item["image-attribution"] && (
            <span styleName="attribute" dangerouslySetInnerHTML={{ __html: item["image-attribution"] }} />
          )}
        </div>
      )}
    </div>
  ));

  return (
    <div styleName="wrapper">
      <ScrollSnap
        activeIndex={currentIndex}
        onChange={handleIndexChange}
        leftArrow={
          <div className="arrow-wrapper" style={{ top: arrowTop, left: "0px", position: "absolute" }}>
            <TriangleArrow />
          </div>
        }
        rightArrow={
          <div className="arrow-wrapper" style={{ top: arrowTop, right: "0px", position: "absolute" }}>
            <TriangleArrow />
          </div>
        }
        handleNextClick={handlePageView}
      >
        {children}
      </ScrollSnap>
    </div>
  );
};

Slideshow.propTypes = {
  element: object,
  auto: bool
};

export default Slideshow;
